import { c as _c } from "react/compiler-runtime";
import { ADVISORS_BASE } from '@src/components/sco/Advisors/constants';
import { normalizeAsPath } from '@src/components/sco/Advisors/utils';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

/**
 * Standalone product root. Prefer `useAdvisorsPaths()` / `resolveAdvisorsBase()` so
 * Request Workspace embeds (`/requests/manage/:id/advisors`) keep links under that prefix.
 */
export const ADVISORS_STANDALONE_BASE = ADVISORS_BASE;
const MANAGE_ADVISORS_BASE_RE = /^(\/requests\/manage\/[^/]+\/advisors)(?=\/|$)/;

/** True when Advisors is mounted under Request manage workspace. */
export const isAdvisorsEmbeddedPath = (asPath: string): boolean => MANAGE_ADVISORS_BASE_RE.test(normalizeAsPath(asPath));

/**
 * Resolve the Advisors URL base for the current location.
 * - `/advisors...` → `/advisors`
 * - `/requests/manage/:id/advisors...` → `/requests/manage/:id/advisors`
 */
export const resolveAdvisorsBase = (asPathOrPathname: string): string => {
  const path = normalizeAsPath(asPathOrPathname);
  const manageMatch = path.match(MANAGE_ADVISORS_BASE_RE);
  if (manageMatch) {
    return manageMatch[1];
  }
  return ADVISORS_STANDALONE_BASE;
};

/** Join base + relative segments without double slashes. */
export const advisorsJoin = (base: string, ...segments: string[]): string => {
  let result = base.replace(/\/+$/, '') || '';
  for (const seg of segments) {
    if (seg == null || seg === '') {
      continue;
    }
    const clean = String(seg).replace(/^\/+/, '').replace(/\/+$/, '');
    if (clean) {
      result += `/${clean}`;
    }
  }
  return result || '/';
};

/** Path under Advisors base, e.g. advisorsSubpath('/advisors/directory/foo', '/advisors') → '/directory/foo'. */
export const advisorsSubpath = (asPathOrPathname: string, base?: string): string => {
  const path = normalizeAsPath(asPathOrPathname);
  const root = base ?? resolveAdvisorsBase(path);
  if (path === root || path === `${root}/`) {
    return '';
  }
  if (path.startsWith(`${root}/`)) {
    return path.slice(root.length);
  }
  return path;
};
export type AdvisorsPaths = {
  base: string;
  ask: string;
  /** Unified conversations inbox (list left, chat right). */
  inbox: string;
  askAdvisors: string;
  seats: string;
  /**
   * Legacy seat *list* URLs. Pages redirect into My Advisors; keep the paths so
   * old links and `alsoActive` prefix-matching on `/seats` still resolve.
   */
  seatsDrafts: string;
  seatsSearching: string;
  seatsInvited: string;
  seatsFilled: string;
  seatNew: string;
  opportunities: string;
  /** Opportunities sub-sections (own pages, shared sub-nav rail). */
  opportunityInvites: string;
  opportunityApplications: string;
  opportunityMatches: string;
  opportunityEngagements: string;
  myAdvisors: string;
  /** My Advisors status views (own pages, shared sub-nav rail). */
  myAdvisorsAll: string;
  myAdvisorsInvited: string;
  myAdvisorsSearching: string;
  myAdvisorsDrafts: string;
  myFounders: string;
  myFoundersAll: string;
  upgrade: string;
  sessions: string;
  /** Book flow under Sessions. */
  sessionNew: string;
  /** Rates live under My Advisor Profile → Settings (legacy `/sessions/settings` redirects). */
  sessionSettings: string;
  /** Payouts live under My Advisor Profile → Payouts (legacy `/payouts` redirects). */
  payouts: string;
  browse: string;
  directory: string;
  /** Public directory prefix — use `${profile}/${slug}`. */
  profile: string;
  /**
   * Own marketplace profile hub root (`/profile`) — Setup.
   * Public cards stay `${profile}/${memberSlug}`.
   */
  ownProfile: string;
  /** Own profile preview (`/profile/view`). */
  profileView: string;
  /** Jump-anywhere editor (`/profile/edit`). */
  profileEdit: string;
  /** Booking hours, session rates, match-email prefs (`/profile/settings`). */
  profileSettings: string;
  /** Stripe Connect, balances, payout history (`/profile/payouts`). */
  profilePayouts: string;
  /** Manage session reviews about you (`/profile/reviews`). */
  profileReviews: string;
  apply: string;
  /** Prefix for the FAST editor (`…/agreements/edit`). Prefer {@link agreementEdit}. */
  agreement: string;
  /** FAST agreement editor for an id (`…/agreements/edit/{id}`). */
  agreementEdit: (agreementId: string) => string;
  agreements: string;
  sign: string;
  /** Alias of `apply` so leftover links keep working. */
  becomeAdvisor: string;
  /** Public marketing LP for people who want to become advisors. Standalone only. */
  becomeAStartupAdvisor: string;
  /** Clarity expert LP + import diagnostic (`/become-a-startup-advisor/clarity/{screenname}`). Standalone only. */
  becomeAnAdvisorClarity: (screenname: string) => string;
  /** Alias of {@link becomeAnAdvisorClarity}. */
  clarityImport: (handle: string) => string;
  compCalculator: string;
  /**
   * Logged-out marketing entry (`/advisors`). `/advisors/landing` always shows the LP.
   * `/advisors/v2` 301s here. Standalone only.
   */
  landing: string;
  /** Join segments under current base. */
  path: (...segments: string[]) => string;
  /** Append query string to an href. */
  withQuery: (href: string, query?: Record<string, string | undefined | null>) => string;
  /** Whether this base is the Request Workspace embed. */
  embedded: boolean;
};
export const buildAdvisorsPaths = (base: string = ADVISORS_STANDALONE_BASE): AdvisorsPaths => {
  const path = (...segments: string[]) => advisorsJoin(base, ...segments);
  const withQuery = (href: string, query?: Record<string, string | undefined | null>) => {
    if (!query) {
      return href;
    }
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value != null && value !== '') {
        params.set(key, value);
      }
    }
    const qs = params.toString();
    if (!qs) {
      return href;
    }
    return href.includes('?') ? `${href}&${qs}` : `${href}?${qs}`;
  };
  return {
    base,
    ask: path('ask'),
    inbox: path('inbox'),
    askAdvisors: path('ask-advisors'),
    seats: path('seats'),
    seatsDrafts: path('seats', 'drafts'),
    seatsSearching: path('seats', 'searching'),
    seatsInvited: path('seats', 'invited'),
    seatsFilled: path('seats', 'filled'),
    seatNew: path('seats', 'new'),
    opportunities: path('opportunities'),
    opportunityInvites: path('opportunities', 'invites'),
    opportunityApplications: path('opportunities', 'applications'),
    opportunityMatches: path('opportunities', 'matches'),
    opportunityEngagements: path('opportunities', 'engagements'),
    myAdvisors: path('my-advisors'),
    myAdvisorsAll: path('my-advisors', 'all'),
    myAdvisorsInvited: path('my-advisors', 'invited'),
    myAdvisorsSearching: path('my-advisors', 'searching'),
    myAdvisorsDrafts: path('my-advisors', 'drafts'),
    myFounders: path('my-founders'),
    myFoundersAll: path('my-founders', 'all'),
    upgrade: path('upgrade'),
    sessions: path('sessions'),
    sessionNew: path('sessions', 'new'),
    sessionSettings: path('profile', 'settings'),
    payouts: path('profile', 'payouts'),
    browse: path('browse'),
    directory: path('directory'),
    profile: path('profile'),
    ownProfile: path('profile'),
    profileView: path('profile', 'view'),
    profileEdit: path('profile', 'edit'),
    profileSettings: path('profile', 'settings'),
    profilePayouts: path('profile', 'payouts'),
    profileReviews: path('profile', 'reviews'),
    apply: path('apply'),
    agreement: path('agreements', 'edit'),
    agreementEdit: (agreementId: string) => path('agreements', 'edit', encodeURIComponent(agreementId)),
    agreements: path('agreements'),
    sign: path('sign'),
    becomeAdvisor: path('apply'),
    becomeAStartupAdvisor: `${ADVISORS_STANDALONE_BASE}/become-a-startup-advisor`,
    becomeAnAdvisorClarity: (screenname: string) => `${ADVISORS_STANDALONE_BASE}/become-a-startup-advisor/clarity/${encodeURIComponent(screenname)}`,
    clarityImport: (handle: string) => `${ADVISORS_STANDALONE_BASE}/become-a-startup-advisor/clarity/${encodeURIComponent(handle)}`,
    compCalculator: path('comp-calculator'),
    // Marketing landing is standalone-only (not embedded in Request Workspace).
    landing: ADVISORS_STANDALONE_BASE,
    path,
    withQuery,
    embedded: base !== ADVISORS_STANDALONE_BASE && base.startsWith('/requests/manage/')
  };
};

/** Default standalone paths (same values as legacy constants). */
export const STANDALONE_ADVISORS_PATHS = buildAdvisorsPaths(ADVISORS_STANDALONE_BASE);

/**
 * Advisors paths for the current router location.
 * Use for all in-app Advisors links so embeds stay under `/requests/manage/.../advisors`.
 */
export const useAdvisorsPaths = () => {
  const $ = _c(4);
  const router = useRouter();
  const asPath = router.asPath || router.pathname || ADVISORS_STANDALONE_BASE;
  let t0;
  if ($[0] !== asPath) {
    t0 = resolveAdvisorsBase(asPath);
    $[0] = asPath;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const base = t0;
  let t1;
  if ($[2] !== base) {
    t1 = buildAdvisorsPaths(base);
    $[2] = base;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};

/** Request manage Advisors base for a group request id. */
export const requestManageAdvisorsBase = (groupRequestId: string) => `/requests/manage/${groupRequestId}/advisors`;